import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Layers, Link2, Plus, Shield, X } from 'lucide-react';
import { ApiError, pages as pagesApi, type PageSummary } from '@/lib/api';
import { DashboardLayout } from '@/components/DashboardLayout';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import {
  Badge,
  ButtonLink,
  DataRow,
  EmptyState,
} from '@/components/ui';

export default function Pages() {
  const [list, setList] = useState<PageSummary[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<PageSummary | null>(null);
  const navigate = useNavigate();

  function reload() {
    pagesApi
      .list()
      .then((res) => setList(res.pages))
      .catch((e) => setErr((e as Error).message || 'Failed to load pages.'));
  }
  useEffect(() => {
    reload();
  }, []);

  async function doDelete() {
    if (!pendingDelete) return;
    try {
      await pagesApi.delete(pendingDelete.id);
      reload();
    } catch (e) {
      // The Delete button is gated on linkCount === 0 in the UI, but if the
      // count was stale (another tab attached a link in the meantime) the
      // server still rejects with `page_in_use`. Surface that as a friendly
      // refresh prompt rather than a raw error message.
      if (e instanceof ApiError && e.code === 'page_in_use') {
        setErr('This page is now in use by a link — refresh to see which one(s).');
      } else {
        setErr((e as Error).message);
      }
    }
  }

  const titleAction = list && list.length > 0 ? (
    <ButtonLink to="/app/pages/new" variant="primary" size="sm" leadingIcon={<Plus size={16} />}>
      New page
    </ButtonLink>
  ) : null;

  return (
    <DashboardLayout title="Landing pages" titleAction={titleAction}>
      <p className="mb-7 max-w-2xl text-[var(--color-on-surface-variant)]">
        Build a quick page and attach it to a link instead of a redirect.
        Pages live at any of your short links and render server-side.
      </p>

      {err && <p className="mb-4 text-sm text-[var(--color-destructive)]">{err}</p>}

      {list === null && !err && (
        <div className="space-y-3">
          <div className="h-20 animate-pulse rounded-2xl bg-[var(--surface-glass)]" />
          <div className="h-20 animate-pulse rounded-2xl bg-[var(--surface-glass)]" />
        </div>
      )}

      {list && list.length === 0 && (
        <EmptyState
          icon={<Layers size={36} className="text-[var(--color-primary)]" />}
          title="No pages yet"
          description="Start from a template — link tree, single-CTA, or app install — and edit blocks in place. Each page can be attached to any link."
          cta={
            <ButtonLink to="/app/pages/new" variant="primary" size="lg">
              Create your first page
            </ButtonLink>
          }
        />
      )}

      {list && list.length > 0 && (
        <ul className="space-y-2.5">
          {list.map((p) => {
            const inUse = p.linkCount > 0 || p.decoyCount > 0;
            // Build the "why this can't be deleted" hint listing both
            // kinds of usage, so the customer knows exactly what to
            // detach before the delete unblocks.
            const inUseParts: string[] = [];
            if (p.linkCount > 0) {
              inUseParts.push(
                `${p.linkCount} link${p.linkCount === 1 ? '' : 's'} routing here`,
              );
            }
            if (p.decoyCount > 0) {
              inUseParts.push(
                `${p.decoyCount} Poof Shield decoy${p.decoyCount === 1 ? '' : 's'}`,
              );
            }
            const inUseHint = inUse
              ? `Remove or reassign before deleting: ${inUseParts.join(' · ')}.`
              : '';
            return (
              <DataRow
                key={p.id}
                to={`/app/pages/${p.id}`}
                trailing={
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (inUse) return;
                      setPendingDelete(p);
                    }}
                    disabled={inUse}
                    title={inUseHint || 'Delete this page'}
                    className={`flex items-center gap-1.5 px-4 text-xs font-medium transition ${
                      inUse
                        ? 'cursor-not-allowed text-[var(--color-on-surface-variant)]/40'
                        : 'text-[var(--color-on-surface-variant)] hover:bg-[var(--surface-glass)] hover:text-[var(--color-destructive)]'
                    }`}
                  >
                    <X size={13} />
                    <span className="hidden sm:inline">Delete</span>
                  </button>
                }
              >
                <DataRow.Stack>
                  <DataRow.Title>
                    <span className="truncate">{p.name}</span>
                    <Badge tone={p.status === 'published' ? 'secondary' : 'neutral'}>
                      {p.status}
                    </Badge>
                    {p.linkCount > 0 && (
                      <Badge
                        tone="primary"
                        title={`Used as the lander for ${p.linkCount} link${p.linkCount === 1 ? '' : 's'}`}
                      >
                        <Link2 size={10} /> {p.linkCount} link{p.linkCount === 1 ? '' : 's'}
                      </Badge>
                    )}
                    {p.decoyCount > 0 && (
                      <Badge
                        tone="primary"
                        title={`Used as the Poof Shield decoy for ${p.decoyCount} link${p.decoyCount === 1 ? '' : 's'}`}
                      >
                        <Shield size={10} /> {p.decoyCount} decoy{p.decoyCount === 1 ? '' : 's'}
                      </Badge>
                    )}
                  </DataRow.Title>
                  <DataRow.Meta mono={false}>
                    Updated {new Date(p.updatedAt).toLocaleString()}
                  </DataRow.Meta>
                </DataRow.Stack>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(`/app/pages/${p.id}`);
                  }}
                  className="text-xs font-semibold uppercase tracking-[0.06em] text-[var(--color-primary)] hover:underline"
                >
                  Edit →
                </button>
              </DataRow>
            );
          })}
        </ul>
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        onConfirm={doDelete}
        title={pendingDelete ? `Delete "${pendingDelete.name}"?` : ''}
        description="No links currently use this page, so it's safe to remove. This can't be undone."
        confirmLabel="Delete page"
        destructive
      />
    </DashboardLayout>
  );
}
